import React, { useLayoutEffect, useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import DeckReshuffle from '../components/cache/DeckReshuffle';
import OfferCard from '../components/cache/OfferCard';
import SyncBadge from '../components/cache/SyncBadge';
import QuickCheckDemo from '../components/cache/QuickCheckDemo';
import {
  FreshnessCycleLoop,
  SyncPulseLoop,
  AddCardFlowLoop,
  DealHandLoop,
  SwipeLoop,
  QuickCheckAutoLoop,
} from '../components/cache/AnimationLoops';
import AgentDemoSettingsScreen from '../components/cache/AgentDemoSettingsScreen';
import Shuffle from '../components/cache/Shuffle';
import InteractHint from '../components/cache/InteractHint';
import './CacheProject.css';

gsap.registerPlugin(ScrollTrigger);

const SURFACE_SWATCHES: { name: string; hex: string; usage: string }[] = [
  { name: 'surface-bg', hex: '#FAFAFA', usage: 'page background' },
  { name: 'surface-card', hex: '#FFFFFF', usage: 'cards, sheets' },
  { name: 'surface-elevated', hex: '#F4F5F7', usage: 'wells, empty states' },
  { name: 'surface-overlay', hex: 'rgba(20,23,31,.45)', usage: 'modal & sheet backdrops' },
  { name: 'border', hex: '#E2E4E9', usage: 'all dividers' },
  { name: 'text-primary', hex: '#14171F', usage: 'headings, values' },
  { name: 'text-secondary', hex: '#5B6270', usage: 'body copy' },
  { name: 'text-disabled', hex: '#A0A4AD', usage: 'placeholders' },
];

const ACCENT_SWATCH = { name: 'color-primary', hex: '#00AE74', usage: 'buttons & links only, never decorative' };

const FRESHNESS_FILL_SWATCHES: { name: string; hex: string; usage: string }[] = [
  { name: 'color-fresh', hex: '#16A34A', usage: 'dot · synced <24h' },
  { name: 'color-aging', hex: '#F59E0B', usage: 'dot · synced 24-72h' },
  { name: 'color-stale', hex: '#9CA3AF', usage: 'dot · needs refresh' },
  { name: 'color-syncing', hex: '#60A5FA', usage: 'dot · actively syncing' },
  { name: 'color-error', hex: '#DC2626', usage: 'dot · sync failed' },
];

const FRESHNESS_TEXT_SWATCHES: { name: string; hex: string; usage: string }[] = [
  { name: 'color-fresh-text', hex: '#15803D', usage: '5.0:1 on white · AA' },
  { name: 'color-aging-text', hex: '#B45309', usage: '5.0:1 on white · AA' },
  { name: 'color-stale-text', hex: '#475569', usage: '7.6:1 on white · AA' },
  { name: 'color-syncing-text', hex: '#2563EB', usage: '5.2:1 on white · AA' },
];

const TAG_CHIPS: { name: string; hex: string }[] = [
  { name: 'dining', hex: '#E08E5B' },
  { name: 'travel', hex: '#5B9BD5' },
  { name: 'groceries', hex: '#6B9B4F' },
  { name: 'gas', hex: '#A0826D' },
  { name: 'entertainment', hex: '#B588D9' },
  { name: 'retail', hex: '#E08398' },
  { name: 'other', hex: '#94A3B8' },
];

const WATERFALL_SCALE: { label: string; meta: string; sample: string; style: React.CSSProperties }[] = [
  { label: 'H1', meta: '32 / 40 · 600 · Satoshi', sample: 'Your offers, ranked by you', style: { fontSize: '32px', lineHeight: '40px', fontWeight: 600 } },
  { label: 'H2', meta: '24 / 32 · 500 · Satoshi', sample: 'Quick Check', style: { fontSize: '24px', lineHeight: '32px', fontWeight: 500 } },
  { label: 'H3', meta: '20 / 28 · 500 · Satoshi', sample: 'Synced 2 hours ago', style: { fontSize: '20px', lineHeight: '28px', fontWeight: 500 } },
  { label: 'Body-lg', meta: '17 / 24 · 400 · Satoshi', sample: 'Every card you carry, one feed.', style: { fontSize: '17px', lineHeight: '24px', fontWeight: 400 } },
  { label: 'Body', meta: '15 / 22 · 400 · Satoshi', sample: 'Redeem before it expires to keep the offer from going to waste.', style: { fontSize: '15px', lineHeight: '22px', fontWeight: 400 } },
  { label: 'Body-sm', meta: '13 / 18 · 400 · Satoshi', sample: 'Moved up, you use dining offers often', style: { fontSize: '13px', lineHeight: '18px', fontWeight: 400 } },
  { label: 'Caption', meta: '12 / 16 · 400 · Satoshi', sample: 'EXPIRING SOON', style: { fontSize: '12px', lineHeight: '16px', fontWeight: 400, letterSpacing: '0.06em' } },
];

const NUMERIC_SCALE: { label: string; meta: string; sample: string; style: React.CSSProperties }[] = [
  { label: 'Numeric-lg', meta: '28 / 1.2 · 600 · Satoshi', sample: '5% back', style: { fontSize: '28px', lineHeight: 1.2, fontWeight: 600, fontVariantNumeric: 'tabular-nums' } },
  { label: 'Numeric-sm', meta: '15 / 1.2 · 500 · Satoshi', sample: '2d left', style: { fontSize: '15px', lineHeight: 1.2, fontWeight: 500, fontVariantNumeric: 'tabular-nums' } },
];

interface CacheProjectProps {
  theme?: string;
}

const CacheProject: React.FC<CacheProjectProps> = ({ theme = 'dark' }) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const [copiedHex, setCopiedHex] = useState<string | null>(null);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSwatchClick = (hex: string) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(hex).catch(() => {});
    }
    setCopiedHex(hex);
    if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    copyTimeoutRef.current = setTimeout(() => setCopiedHex(null), 1400);
  };

  const [offerSaved, setOfferSaved] = useState(false);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useLayoutEffect(() => {
    document.body.className = 'light';
    // Restore to the app's real theme state on unmount, not a snapshotted
    // className: with AnimatePresence's exit-transition delay, a DOM
    // snapshot can go stale before cleanup runs and leave body stuck light.
    return () => { document.body.className = theme; };
  }, [theme]);

  useEffect(() => {
    return () => { if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current); };
  }, []);

  useEffect(() => {
    const ctx = gsap.context(() => {

      // Mount animations (visible on load)
      gsap.from('.cache__hero-title', {
        y: 32, opacity: 0, duration: 0.9, ease: 'power3.out', delay: 0.2
      });
      gsap.from('.cache__hero-thesis', {
        y: 22, opacity: 0, duration: 0.8, ease: 'power3.out', delay: 0.35
      });
      gsap.from('.cache__hero-showcase', {
        y: 40, opacity: 0, duration: 1.1, ease: 'power3.out', delay: 0.3
      });
      gsap.from('.cache__meta-item', {
        y: 16, opacity: 0, duration: 0.65, ease: 'power3.out', stagger: 0.07, delay: 0.6
      });

      // Set initial states for scroll elements
      gsap.set('.cache-fade-up', { y: 30, opacity: 0 });
      gsap.set('.cache-fade-left', { x: -52, opacity: 0 });
      gsap.set('.cache-fade-right', { x: 52, opacity: 0 });
      gsap.set('.cache-fade-scale', { scale: 0.93, opacity: 0 });

      ScrollTrigger.batch('.cache-fade-up', {
        onEnter: (els) => gsap.to(els, {
          y: 0, opacity: 1, stagger: 0.09, duration: 0.85, ease: 'power3.out'
        }),
        start: 'top 90%',
        once: true,
      });
      ScrollTrigger.batch('.cache-fade-left', {
        onEnter: (els) => gsap.to(els, {
          x: 0, opacity: 1, stagger: 0.08, duration: 0.95, ease: 'power3.out'
        }),
        start: 'top 88%',
        once: true,
      });
      ScrollTrigger.batch('.cache-fade-right', {
        onEnter: (els) => gsap.to(els, {
          x: 0, opacity: 1, stagger: 0.08, duration: 0.95, ease: 'power3.out'
        }),
        start: 'top 88%',
        once: true,
      });
      ScrollTrigger.batch('.cache-fade-scale', {
        onEnter: (els) => gsap.to(els, {
          scale: 1, opacity: 1, stagger: 0.08, duration: 1, ease: 'power3.out'
        }),
        start: 'top 88%',
        once: true,
      });

    }, rootRef);

    return () => ctx.revert();
  }, []);

  return (
    <div className="cache" ref={rootRef}>

      {/* Hero: keynote-style one-pager, static collage, no text per template rule */}
      <section className="cache__hero">
        <div className="cache__hero-showcase">
          <div className="cache__hero-card cache__hero-card--back-left">
            <OfferCard merchant="Delta" logo="/content/cache/logos/delta.svg" category="travel" value="$50 off" freshness="fresh" expiresIn="5d left" />
          </div>
          <div className="cache__hero-card cache__hero-card--back-right">
            <OfferCard merchant="Target" logo="/content/cache/logos/target.svg" category="groceries" value="3% back" freshness="aging" expiresIn="2d left" />
          </div>
          <div className="cache__hero-card cache__hero-card--front">
            <OfferCard merchant="Starbucks" logo="/content/cache/logos/starbucks.svg" category="dining" value="5% back" freshness="fresh" expiresIn="6d left" saved />
          </div>
          <div className="cache__hero-float cache__hero-float--badge">
            <SyncBadge showHint={false} />
          </div>
          <div className="cache__hero-float cache__hero-float--tags">
            <span className="cache__deck-tag cache__deck-tag--dining">dining</span>
            <span className="cache__deck-tag cache__deck-tag--travel">travel</span>
            <span className="cache__deck-tag cache__deck-tag--groceries">groceries</span>
          </div>
        </div>
      </section>

      <div className="cache__container">

        {/* Header: title, thesis, meta, moved below the hero per template rule (no text in hero strip) */}
        <header className="cache__header">
          <h1 className="cache__hero-title">Cache</h1>
          <p className="cache__hero-thesis">
            Your cards already have the offer. You just never see it.
            Cache is a concept design system for a rewards app: it turns a list of discounts you'd normally miss into a deck of cards that reorders itself around what you actually spend on.
          </p>
          <div className="cache__meta">
            {[
              { label: 'Type', value: 'Design System' },
              { label: 'Year', value: '2026' },
              { label: 'Role', value: 'Product Designer' },
              { label: 'Tools', value: 'Figma, React, GSAP' },
            ].map(({ label, value }) => (
              <div className="cache__meta-item" key={label}>
                <div className="cache__meta-label">{label}</div>
                <div className="cache__meta-value">{value}</div>
              </div>
            ))}
          </div>
        </header>

        {/* The Problem */}
        <section className="cache__section cache__section--no-top-border">
          <div className="cache__section-label cache-fade-up">the problem</div>
          <p className="cache__statement cache__statement--narrow cache-fade-up">
            I carry four cards and two bank accounts, and each one hides its offers in a different app. Checking all of them before every purchase costs more effort than most offers are worth, so I stopped checking, and discounts I already qualified for went unused.
          </p>
        </section>

        {/* The Thesis */}
        <section className="cache__section cache__section--thesis">
          <div className="cache__section-label cache-fade-up">the thesis</div>
          <p className="cache__thesis-statement cache-fade-up">
            A deck of cards, reshuffled by what you actually spend on.
          </p>
          <p className="cache__thesis-support cache-fade-up">
            Not a list to scroll and not a filter to set once. Cache borrows the physical logic of a card game, cards fan, get dealt, get reshuffled, and applies it to something that has never had a visual identity of its own: the offer you already qualify for but never see.
          </p>
          <div className="cache__principles cache-fade-up">
            {[
              { label: 'Physical, not abstract', desc: 'Cards fan and overlap like a hand you are holding, not rows in a scrollable list.' },
              { label: 'Personalized, not static', desc: 'Ranking reflects what you actually redeem, not a category filter set once at signup.' },
              { label: 'Explained, not silent', desc: 'Every reorder shows its reason on screen, so personalization never reads as a black box.' },
            ].map(p => (
              <div className="cache__principle" key={p.label}>
                <div className="cache__principle-label">{p.label}</div>
                <p className="cache__principle-desc">{p.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Moodboard: a scrapbook-style collage per cluster, references pinned like stickers */}
        <section className="cache__section cache__moodboard">
          <div className="cache__section-label">moodboard</div>
          <div className="cache__moodboard-grid">
            {[
              {
                title: 'the deck metaphor',
                tiles: [
                  { label: 'playing cards fanned in hand', image: '/content/cache/moodboard/cards-fanned-in-hand.jpg' },
                  { label: 'solitaire app card stack', image: '/content/cache/moodboard/solitaire-card-stack.png' },
                  { label: 'tarot reading spread layout', image: '/content/cache/moodboard/tarot-spread.png' },
                  { label: 'loyalty card wallet stack', image: '/content/cache/moodboard/wallet-card-stack.jpg' },
                ],
                took: 'The physical overlap and depth of a held hand of cards as the base interaction metaphor.',
                leftOut: 'Literal card-back patterns, suit iconography, and any gambling or casino visual language.',
                credit: 'hand: photo by Alessandro Bogliari on Unsplash · solitaire, tarot, and wallet: reference screenshots',
              },
              {
                title: 'data as trust, not decoration',
                tiles: [
                  { label: 'banking app balance display', image: '/content/cache/moodboard/banking-balance-display.webp' },
                  { label: 'flight status sync indicator', image: '/content/cache/moodboard/flight-status-sync.webp' },
                  { label: 'weather app tabular readout', image: '/content/cache/moodboard/weather-tabular-readout.png' },
                  { label: 'utility dashboard status dot', image: '/content/cache/moodboard/dashboard-status-dot.png' },
                ],
                took: 'Tabular numerals and small, unobtrusive status dots: the visual language of tools people already trust with real data.',
                leftOut: 'Gamified badges, streak counters, and celebratory animation that would undercut a finance-adjacent product’s credibility.',
                credit: 'reference screenshots: Apple Wallet/Weather/Flighty and a Carbon Design System status table',
              },
            ].map((cluster, ci) => (
              <div className={`cache__moodboard-cluster cache__moodboard-cluster--${ci}`} key={cluster.title}>
                <div className="cache__moodboard-tiles cache__moodboard-tiles--photos">
                  {cluster.tiles.map(tile => (
                    <div className="cache__moodboard-tile cache__moodboard-tile--photo" key={tile.label}>
                      <img className="cache__moodboard-tile-img" src={tile.image} alt={tile.label} loading="lazy" />
                    </div>
                  ))}
                </div>
                <p className="cache__moodboard-note">
                  <strong>Took:</strong> {cluster.took} <strong>Left out:</strong> {cluster.leftOut}
                </p>
                <span className="cache__moodboard-credit">{cluster.credit}</span>
              </div>
            ))}
          </div>
        </section>

        {/* The System: Foundations */}
        <section className="cache__section">
          <div className="cache__section-label cache-fade-up">the system, foundations</div>
          <p className="cache__statement cache__statement--narrow cache-fade-up">
            Every color and type size on this page is pulled live from the same underlying tokens.
          </p>

          <div className="cache__foundations cache-fade-up">

            <div className="cache__foundations-row cache__foundations-row--surfaces-accent">
              {/* Surfaces & text: swatch paired with where it's actually used */}
              <div className="cache__foundations-group cache__foundations-group--surfaces">
                <div className="cache__foundations-group-title">surfaces &amp; text</div>
                <div className="cache__swatch-grid cache__swatch-grid--4col">
                  {SURFACE_SWATCHES.map(({ name, hex, usage }) => (
                    <button
                      type="button"
                      className="cache__swatch"
                      key={name}
                      onClick={() => handleSwatchClick(hex)}
                      aria-label={`Copy ${name} (${hex}) to clipboard`}
                    >
                      <span className="cache__swatch-color" style={{ background: hex }} />
                      <span className="cache__swatch-info">
                        <span className="cache__swatch-name">{name}</span>
                        <span className="cache__swatch-hex">{copiedHex === hex ? 'Copied ✓' : hex}</span>
                        <span className="cache__swatch-usage">{usage}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Primary accent: single hero tile, deliberately alone */}
              <div className="cache__foundations-group cache__foundations-group--accent">
                <div className="cache__foundations-group-title">primary accent</div>
                <button
                  type="button"
                  className="cache__accent-tile"
                  onClick={() => handleSwatchClick(ACCENT_SWATCH.hex)}
                  aria-label={`Copy ${ACCENT_SWATCH.name} (${ACCENT_SWATCH.hex}) to clipboard`}
                >
                  <span className="cache__accent-tile-color" style={{ background: ACCENT_SWATCH.hex }} />
                  <span className="cache__accent-tile-info">
                    <span className="cache__accent-tile-name">{ACCENT_SWATCH.name}</span>
                    <span className="cache__accent-tile-hex">{copiedHex === ACCENT_SWATCH.hex ? 'Copied ✓' : ACCENT_SWATCH.hex}</span>
                    <span className="cache__accent-tile-usage">{ACCENT_SWATCH.usage}</span>
                  </span>
                </button>
              </div>
            </div>

            <div className="cache__foundations-row">
              {/* Freshness, fill: same swatch-card pattern as surfaces */}
              <div className="cache__foundations-group">
                <div className="cache__foundations-group-title">freshness, fill (dots, badges, backgrounds)</div>
                <div className="cache__swatch-grid">
                  {FRESHNESS_FILL_SWATCHES.map(({ name, hex, usage }) => (
                    <button
                      type="button"
                      className="cache__swatch"
                      key={name}
                      onClick={() => handleSwatchClick(hex)}
                      aria-label={`Copy ${name} (${hex}) to clipboard`}
                    >
                      <span className="cache__swatch-color" style={{ background: hex }} />
                      <span className="cache__swatch-info">
                        <span className="cache__swatch-name">{name}</span>
                        <span className="cache__swatch-hex">{copiedHex === hex ? 'Copied ✓' : hex}</span>
                        <span className="cache__swatch-usage">{usage}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Freshness, text: same swatch-card pattern as surfaces */}
              <div className="cache__foundations-group">
                <div className="cache__foundations-group-title">freshness, text (4.5:1+ on surface-card)</div>
                <div className="cache__swatch-grid">
                  {FRESHNESS_TEXT_SWATCHES.map(({ name, hex, usage }) => (
                    <button
                      type="button"
                      className="cache__swatch"
                      key={name}
                      onClick={() => handleSwatchClick(hex)}
                      aria-label={`Copy ${name} (${hex}) to clipboard`}
                    >
                      <span className="cache__swatch-color" style={{ background: hex }} />
                      <span className="cache__swatch-info">
                        <span className="cache__swatch-name">{name}</span>
                        <span className="cache__swatch-hex">{copiedHex === hex ? 'Copied ✓' : hex}</span>
                        <span className="cache__swatch-usage">{usage}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Category tags: shown as the real chip, not an abstract swatch */}
            <div className="cache__foundations-group">
              <div className="cache__foundations-group-title">category tags</div>
              <div className="cache__tag-chip-row">
                {TAG_CHIPS.map(({ name, hex }) => (
                  <button
                    type="button"
                    className="cache__tag-chip-swatch"
                    key={name}
                    style={{ background: hex }}
                    onClick={() => handleSwatchClick(hex)}
                    aria-label={`Copy tag-${name} (${hex}) to clipboard`}
                  >
                    {copiedHex === hex ? 'Copied ✓' : name}
                  </button>
                ))}
              </div>
            </div>

          </div>

          <div className="cache__type-scale cache-fade-up">
            <Shuffle
              text="type scale: Satoshi (UI) / Geist Pixel (display only)"
              tag="div"
              className="cache__foundations-group-title"
              textAlign="left"
              duration={0.35}
              shuffleDirection="right"
              animationMode="evenodd"
              shuffleTimes={1}
              ease="power3.out"
              stagger={0.03}
              threshold={0.1}
              triggerOnce={true}
              triggerOnHover={true}
              respectReducedMotion={true}
            />

            {/* Display gets the hero specimen treatment, the one place Geist Pixel appears */}
            <div className="cache__type-specimen">
              <span className="cache__type-specimen-sample">Cache</span>
              <span className="cache__type-specimen-meta">Display · 40 / 48 · Geist Pixel</span>
            </div>

            {/* Waterfall: true size, largest to smallest */}
            <div className="cache__type-waterfall">
              {WATERFALL_SCALE.map(row => (
                <div className="cache__type-row" key={row.label}>
                  <span className="cache__type-row-label">{row.label}</span>
                  <span className="cache__type-sample" style={row.style}>{row.sample}</span>
                  <span className="cache__type-meta">{row.meta}</span>
                </div>
              ))}
            </div>

            {/* Numeric: called out separately, it's a distinct functional axis (tabular figures) */}
            <div className="cache__type-numeric">
              <div className="cache__type-numeric-label">tabular numerals</div>
              <div className="cache__type-numeric-row">
                {NUMERIC_SCALE.map(row => (
                  <div className="cache__type-numeric-item" key={row.label}>
                    <span className="cache__type-sample" style={row.style}>{row.sample}</span>
                    <span className="cache__type-meta">{row.label} · {row.meta}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Signature Components: paired Design ↔ System blocks */}
        <section className="cache__section cache__signature">
          <div className="cache__section-label cache-fade-up">signature components</div>
          <p className="cache__statement cache__statement--narrow cache-fade-up">
            Every component pairs a visible design decision with the system logic underneath it.
          </p>

          {/* 1: Offer Card (visual left) */}
          <div className="cache__pair cache__pair--no-top-border">
            <div className="cache__pair-visual cache-fade-left">
              <div className="cache__pair-title">Offer Card</div>
              <div className="cache__pair-frame">
                <OfferCard
                  merchant="Target"
                  logo="/content/cache/logos/target.svg"
                  category="groceries"
                  value="3% back"
                  freshness="fresh"
                  expiresIn="4d left"
                  saved={offerSaved}
                  onToggleSave={() => setOfferSaved(v => !v)}
                />
                <InteractHint />
              </div>
              <p className="cache__pair-rationale">
                Merchant, value, and category read first. Freshness is a quiet dot, not a badge, and it should never demand attention until you are already looking for it.
              </p>
            </div>
            <div className="cache__pair-system cache-fade-right">
              <div className="cache__pair-system-label">system</div>
              <p className="cache__pair-system-prose">
                Freshness and lifecycle are separate axes. A card can be stale and still expiring soon: the two states never collapse into one enum.
              </p>
              <pre className="cache__pair-code">{`freshness(offer) =
  now - offer.syncedAt <  24h ? "fresh"
: now - offer.syncedAt <  72h ? "aging"
: "stale"`}</pre>
            </div>
          </div>

          {/* 2: Sync Status Badge (flip: system left) */}
          <div className="cache__pair cache__pair--flip">
            <div className="cache__pair-system cache-fade-left">
              <div className="cache__pair-system-label">system</div>
              <p className="cache__pair-system-prose">
                Badge state is derived, never set directly by a screen: one source of truth, so two screens can never disagree about whether a card is connected.
              </p>
              <pre className="cache__pair-code">{`status =
  lastAttempt.failed ? "failed"
: token.expired      ? "needs-reauth"
: sync.inFlight       ? "syncing"
: "synced"`}</pre>
            </div>
            <div className="cache__pair-visual cache-fade-right">
              <div className="cache__pair-title">Sync Status Badge</div>
              <div className="cache__pair-frame">
                <SyncBadge />
              </div>
              <p className="cache__pair-rationale">
                Sync status and offer freshness look similar but answer different questions: one is about the connection, the other about the data. Conflating them was the first mistake this system rules out.
              </p>
            </div>
          </div>

          {/* 3: Quick Check (visual left) */}
          <div className="cache__pair">
            <div className="cache__pair-visual cache-fade-left">
              <div className="cache__pair-title">Quick Check</div>
              <div className="cache__pair-frame cache__pair-frame--tall">
                <QuickCheckDemo />
              </div>
              <p className="cache__pair-rationale">
                One box, one answer. Quick Check does not ask you to browse, it deals the single best match face-up, like flipping one card instead of laying out the whole hand.
              </p>
            </div>
            <div className="cache__pair-system cache-fade-right">
              <div className="cache__pair-system-label">system</div>
              <p className="cache__pair-system-prose">
                Matching returns exactly one card. When a merchant has offers on multiple linked cards, they collapse into one expandable result instead of racing to the top of a list.
              </p>
              <pre className="cache__pair-code">{`match(query) =
  offers
    .filter(o => fuzzy(o.merchant, query))
    .sort(byValueDesc)[0]`}</pre>
            </div>
          </div>

          {/* 4: Personalization / reshuffle (flip: system left) */}
          <div className="cache__pair cache__pair--flip">
            <div className="cache__pair-system cache-fade-left">
              <div className="cache__pair-system-label">system</div>
              <p className="cache__pair-system-prose">
                Ranking blends an explicit onboarding signal with implicit redemption behavior, decayed by recency so old preferences fade instead of locking in. Deeper dive below.
              </p>
              <pre className="cache__pair-code">{`rank(offer) =
  w1 * explicit_category_preference
+ w2 * redemption_recency_decay(offer.category)`}</pre>
            </div>
            <div className="cache__pair-visual cache-fade-right">
              <div className="cache__pair-title">Personalization: the reshuffle</div>
              <div className="cache__pair-frame">
                <DeckReshuffle size="compact" interval={3600} />
              </div>
              <p className="cache__pair-rationale">
                Reordering is physical, not a list re-sort. The label underneath always says why, since silent black-box reordering feels manipulative in anything finance-adjacent.
              </p>
            </div>
          </div>
        </section>

        {/* Animation Library */}
        <section className="cache__section cache__animlib">
          <div className="cache__section-label cache-fade-up">animation library</div>
          <p className="cache__statement cache__statement--narrow cache-fade-up">
            Not every component gets a loop. Motion is reserved for moments that carry real meaning, so this grid is deliberate, not exhaustive.
          </p>

          <div className="cache__animgrid cache-fade-up">
            {[
              {
                name: 'Deck reshuffle',
                trigger: 'Triggered on a redemption event',
                legend: 'hero loop · reshuffle token',
                node: <DeckReshuffle size="compact" interval={3200} />,
              },
              {
                name: 'Quick Check deal',
                trigger: 'Triggered by a merchant search',
                legend: 'hero loop · deal-in token',
                node: <QuickCheckAutoLoop />,
              },
              {
                name: 'Offer Card freshness cycle',
                trigger: 'Looped for demo only, in product this reflects real cache age, it never loops on its own',
                legend: 'fresh · aging · stale · syncing',
                node: <FreshnessCycleLoop />,
              },
              {
                name: 'Sync status pulse',
                trigger: 'Active while a linked card is syncing',
                legend: 'sync-pulse token · breathing state',
                node: <SyncPulseLoop />,
              },
              {
                name: 'Add Card flow',
                trigger: 'Issuer → card type → confirm, sped up for demo',
                legend: '3-step flow · no full card number ever asked',
                node: <AddCardFlowLoop />,
              },
              {
                name: 'Onboarding: deal yourself a hand',
                trigger: 'Category preference picker at signup',
                legend: 'explicit signal → personalization ranking',
                node: <DealHandLoop />,
              },
              {
                name: 'Save / dismiss swipe',
                trigger: 'Swipe gesture on a single focused card',
                legend: 'card-swipe token · card-game-native gesture',
                node: <SwipeLoop />,
              },
            ].map(item => (
              <div className="cache__animtile" key={item.name}>
                <div className="cache__animtile-frame">{item.node}</div>
                <div className="cache__animtile-meta">
                  <div className="cache__animtile-name">{item.name}</div>
                  <div className="cache__animtile-trigger">{item.trigger}</div>
                  <div className="cache__animtile-legend">{item.legend}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="cache__animrestraint cache-fade-up">
            <div className="cache__foundations-group-title">shown static, on purpose</div>
            <ul className="cache__animrestraint-list">
              <li><strong>Category Tag</strong>: instant toggle only; looping would look glitchy.</li>
              <li><strong>Toast / notification</strong>: enters, holds, exits once; a looping toast reads as broken.</li>
              <li><strong>Empty state</strong>: a static illustration, at most one subtle idle detail.</li>
              <li><strong>Trust / Permission Panel</strong>: a reading moment; motion would undercut the "we're being careful with your data" tone.</li>
              <li><strong>Masked Card Display</strong>: static; it's a trust moment too.</li>
              <li><strong>Countdown / expiry indicator</strong>: numbers ticking down is real-time truth, not decorative motion.</li>
              <li><strong>Tab bar / nav</strong>: standard interaction states only.</li>
            </ul>
          </div>
        </section>

        {/* The Personalization Engine: deep dive */}
        <section className="cache__section cache__personalization">
          <div className="cache__section-label cache-fade-up">the personalization engine</div>
          <p className="cache__statement cache__statement--narrow cache-fade-up">
            Offers don't change fast enough to animate. What you actually spend on does.
          </p>
          <p className="cache__body cache__personalization-intro cache-fade-up">
            Freshness updates quietly in the background, and it is not something to watch happen. Personalized ranking is the more meaningful, more animatable idea: the deck visibly reorders itself based on real redemption behavior, not a static category filter set once at signup and forgotten.
          </p>

          <div className="cache__pair cache__pair--personalization">
            <div className="cache__pair-visual cache-fade-left">
              <div className="cache__pair-title">Reshuffle, explained every time</div>
              <div className="cache__pair-frame cache__pair-frame--tall">
                <DeckReshuffle interval={3800} />
              </div>
              <p className="cache__pair-rationale">
                Two signals feed the deck: what you picked at signup, and what you have actually redeemed since. Recent redemptions count more than old ones, since preferences drift, and the ranking drifts with them.
              </p>
            </div>
            <div className="cache__pair-system cache-fade-right">
              <div className="cache__pair-system-label">system</div>
              <p className="cache__pair-system-prose">
                An explicit signal from onboarding, the categories tapped during "deal yourself a hand," sets the initial weights. Every redemption afterward is an implicit signal, weighted higher the more recent it is, so ranking adapts instead of freezing at signup.
              </p>
              <pre className="cache__pair-code">{`rank(offer) =
  w1 * explicit_category_preference
+ w2 * redemption_recency_decay(offer.category)`}</pre>
              <p className="cache__pair-system-prose cache__pair-system-prose--spaced">
                Recency decay borrows the same idea as cache freshness elsewhere in this system: recent signals matter more, old ones fade, applied to behavior instead of sync state.
              </p>
              <pre className="cache__pair-code">{`redemption_recency_decay(category) =
  Σ  e^(−λ · days_since(redemption))
     for each past redemption in category`}</pre>
            </div>
          </div>

          <blockquote className="cache__pullquote cache-fade-up">
            Reordering must never be a silent black box.
          </blockquote>
          <p className="cache__body cache__personalization-outro cache-fade-up">
            Every reshuffle carries its reason on screen, "moved up because you use dining offers often," instead of asking users to trust an invisible algorithm. In a finance-adjacent product, an unexplained reorder reads as manipulation, not intelligence.
          </p>
        </section>

        {/* Built for Agentic Workflows */}
        <section className="cache__section cache__agentic">
          <div className="cache__section-label cache-fade-up">built for agentic workflows</div>
          <p className="cache__statement cache__statement--narrow cache-fade-up">
            design-system.md is not documentation for humans that happens to be machine-readable. It is written for the machine first.
          </p>
          <p className="cache__body cache__agentic-intro cache-fade-up">
            Every token is a literal CSS custom property block, not a color described in prose. All 15 components follow the identical five-field template, Purpose, Anatomy, States, Variants, Tokens, Do, Don't, so an agent can extract "what states exist for the Sync Status Badge" without ambiguity.
          </p>

          <blockquote className="cache__pullquote cache-fade-up">
            The documentation format was designed to be machine-legible, not just the components themselves.
          </blockquote>

          <div className="cache__agentic-excerpt cache-fade-up">
            <div className="cache__foundations-group-title">design-system.md, excerpt</div>
            <pre className="cache__pair-code cache__pair-code--file">{`# Cache Design System

**Version:** 1.3
**Purpose:** This file defines the complete Cache design system. When
generating UI, use only the tokens, components, and patterns defined
here; do not invent new colors, spacing values, or component
patterns. If something you need is not defined below, flag it rather
than improvising.

...

### 3.4 Masked Card Display, Tier 1
Purpose: Show which physical card an offer or account belongs to
  without exposing sensitive numbers.
Anatomy: issuer wordmark/icon, card network mark, card type label,
  last-4 digits
States: default | selected | disabled
Do: Always mask to last-4; never render a full card number anywhere.
Don't: Animate this component. It's a trust/reading moment.

### 3.13 Trust / Permission Panel, Tier 3
Purpose: Explain what data is used and why, at onboarding and in
  Settings.
Anatomy: headline, per-permission rows (icon, label, one-line
  explanation), primary action
Do: Keep this static, no motion. It's a reading/trust moment.
Don't: Bury the explanation behind a secondary tap.`}</pre>
          </div>

          <div className="cache__agentic-test cache-fade-up">
            <div className="cache__foundations-group-title">the test</div>
            <p className="cache__body">
              To find out whether the file actually works as ground truth, I opened a fresh agent session with only design-system.md attached (no other page, no visual reference) and gave it one prompt:
            </p>
            <blockquote className="cache__prompt">
              "Using only design-system.md as ground truth, build a Data &amp; Security settings screen. Show linked cards with the Masked Card Display pattern, each with sync status, plus a Trust / Permission Panel explaining what data is used and why."
            </blockquote>
            <p className="cache__body cache__agentic-result-label">Result: live, unedited</p>
            <div className="cache__pair-frame cache__pair-frame--tall">
              <AgentDemoSettingsScreen />
            </div>
            <p className="cache__body cache__agentic-note">
              The spec was enough to get the rules right on the first pass: masked-to-last-4 with no full card number anywhere, sync status kept visually separate from the freshness palette, and the permission panel left static with no motion. It did not dictate exact spacing or the row-versus-stacked layout of the card list, and that was a judgment call inside the rules, not a violation of them, which is a reasonable place for a spec like this to leave room open. Worth noting for anyone else building against it: two agents given the same file could still produce visually different, equally compliant results.
            </p>
          </div>
        </section>

        {/* Governance & Scale */}
        <section className="cache__section cache__governance">
          <div className="cache__section-label cache-fade-up">governance &amp; scale</div>
          <ul className="cache__governance-list cache-fade-up">
            <li>Versioning follows the changelog in design-system.md: a token or accessibility fix bumps a minor version, a new component bumps a minor version, a breaking change to an existing component's anatomy would bump a major one.</li>
            <li>New components get proposed by filling out the same five-field template, Purpose, Anatomy, States, Variants, Tokens, Do, Don't, before any visual exploration starts. The spec exists before the pixels do.</li>
            <li>Any new color has to clear the same AA contrast check the freshness palette went through in v1.2, before it is added, not audited retroactively.</li>
            <li>design-system.md and this case study live in the same repository, so the documentation and the components it describes change together instead of drifting apart.</li>
          </ul>
        </section>

        {/* Reflection */}
        <section className="cache__section cache__reflection">
          <div className="cache__section-label cache-fade-up">reflection</div>
          <p className="cache__body cache__reflection-copy cache-fade-up">
            The freshness and lifecycle system probably does more design work than this product needs at its current stage. Five distinct states for one small dot is a lot of nuance for something most users will glance at for half a second, and I would want real usage data before defending all five long-term. The personalization ranking formula is presented here as a design rationale artifact, not tested code; recency decay in particular deserves a real backtest against redemption data before I would trust the weighting. I would also want to pressure-test the deck metaphor with actual card-carrying users outside of a card-game frame of reference. It reads as playful to me, but that is one person's intuition, not research. What I am most confident in is the documentation format itself: writing every component to the same five-field template before designing it was a genuinely useful constraint, not just a nice idea for a case study.
          </p>
        </section>

        {/* Close */}
        <section className="cache__section cache__close">
          <div className="cache__section-label cache-fade-up">projected outcomes</div>
          <p className="cache__body cache__close-caveat cache-fade-up">
            Cache was not deployed, so nothing below is measured. These are hypotheses the design is built to test, not results.
          </p>
          <ul className="cache__outcomes cache-fade-up">
            {[
              { title: 'Faster time-to-offer', desc: 'Quick Check collapses a multi-app search into one query and one answer.' },
              { title: 'Higher redemption of offers already available', desc: 'Ranking surfaces what is actually relevant instead of a flat, unordered list.' },
              { title: 'Lower onboarding drop-off', desc: 'The trust panel and masked-card pattern address data-sharing hesitation before it becomes a blocker.' },
            ].map(({ title, desc }) => (
              <li className="cache__outcome" key={title}>
                <div className="cache__outcome-dot" />
                <div>
                  <span className="cache__outcome-strong">{title}</span>
                  <span className="cache__outcome-desc">{desc}</span>
                </div>
              </li>
            ))}
          </ul>

          <p className="cache__body cache__close-caveat cache-fade-up">
            One part of this is not a hypothesis: markdown-first, agent-readable documentation scales faster than the alternatives, and that is measured, just not by me. Markdown runs 34 to 38% more token-efficient than the same content written as JSON, and heading-based markdown chunking outperforms arbitrary text splitting by up to 35% in retrieval-accuracy benchmarks. Both matter directly for a spec meant to be read by a coding agent on every task, not by a person once. It is also where the tooling itself is heading: GitHub Spec Kit, AWS Kiro, Cursor, and Claude Code have all shipped a spec-driven development mode since 2025, treating the written spec, not the prompt, as the source of truth. None of that proves design-system.md's specific format is correct, but it is real evidence that this shape of documentation is the faster one to scale, at least for the audience actually reading it.
          </p>

          <div className="cache__closing cache-fade-up">
            <p className="cache__closing-copy">
              Cache is a system for one specific idea: an offer feed that reorders itself and tells you why. There is more of it in design-system.md than made it onto this page.
            </p>
          </div>
        </section>

      </div>
    </div>
  );
};

export default CacheProject;
